import type { LayerRequestData } from './use_layer_fetch'
import type { MapViewport, WineryBounds, WineryFilters, WineryLayer } from '../types/atlas'

// What the map reports upward for the WINERY layer: bounds and zoom TOGETHER,
// because the server picks clusters or points from the zoom and answers the
// bounds, and a request built from two separately-reported halves would answer a
// viewport that never existed.
//
// Deliberately not `MapViewport` from `types/atlas`, which is P4's flat
// west/south/east/north frame for the appellation layer. Two layers, two request
// shapes, two names -- one name over both would typecheck at the seam and answer
// the wrong rectangle.
export interface WineryViewport {
  bounds: WineryBounds
  zoom: number
}

// The map reports ONE camera frame (P4's `MapViewport`, already clamped and
// widened to the whole world when it crosses the antimeridian); this is the
// winery layer's own shape of it. `canonicalRequest` re-wraps and re-rounds
// after, which is idempotent on an already-clamped frame -- so a Pacific
// viewport costs payload here rather than answering the wrong rectangle.
export function wineryViewportOf(viewport: MapViewport): WineryViewport {
  return {
    bounds: {
      min_lng: viewport.west,
      min_lat: viewport.south,
      max_lng: viewport.east,
      max_lat: viewport.north,
    },
    zoom: viewport.zoom,
  }
}

export interface WineryRequest {
  bounds: WineryBounds
  zoom: number
  filters: WineryFilters
}

export const EMPTY_WINERY_FILTERS: WineryFilters = {
  size_classes: [],
  independence_statuses: [],
  min_confidence: null,
  direct_sales: false,
  organic: false,
  biodynamic: false,
  visiting: [],
  wine_club: false,
}

// Mirrors `WineryLayerQuery::ECHO_PRECISION`, `ZOOM_RANGE`, `LAT_RANGE` and
// `WORLD`. They are restated rather than shipped as props because they are the
// shape of the ANSWER, not data — see the comment on `canonicalRequest` for why
// the client has to know them at all.
const ECHO_PRECISION = 5
const MAX_ZOOM = 22
const WHOLE_TURN = 360
const WORLD: WineryBounds = { min_lng: -180, min_lat: -90, max_lng: 180, max_lat: 90 }

const round = (value: number) => Math.round(value * 10 ** ECHO_PRECISION) / 10 ** ECHO_PRECISION
const clamp = (value: number, low: number, high: number) => Math.min(Math.max(value, low), high)

// Ruby's `%` floors and JavaScript's truncates, so `(-190 + 180) % 360` is 350
// there and -10 here. Getting this wrong makes every Pacific viewport disagree
// with its own echo, which reads as the layer never drawing rather than as a
// wrap bug.
const wrapLongitude = (value: number) => {
  const wrapped = ((((value + 180) % WHOLE_TURN) + WHOLE_TURN) % WHOLE_TURN) - 180

  return wrapped === 0 && value > 0 ? 180 : wrapped
}

/**
 * The request, in the exact form the server will echo it back.
 *
 * This mirrors `WineryLayerQuery#bounds` on purpose, and the reason is narrow:
 * the server echoes what it ANSWERED — clamped, unwrapped, rounded — so that the
 * client can drop a response it has outrun. If the client compared its raw
 * MapLibre bounds against that echo, every response would look outrun and the
 * layer would never draw at all, which is a worse failure than the one the echo
 * exists to prevent.
 *
 * So the client sends already-canonical numbers and the server's own
 * canonicalization becomes idempotent on them. The server keeps every rule
 * anyway — this is a formatting step, not a validation one, and nothing here is
 * trusted over there.
 */
export function canonicalRequest(viewport: WineryViewport, filters: WineryFilters): WineryRequest {
  const { min_lng: minLng, max_lng: maxLng, min_lat: minLat, max_lat: maxLat } = viewport.bounds

  // Measured BEFORE wrapping, like the server: past a full turn there is no
  // viewport left to name, however the numbers arrived.
  const whole = Math.abs(maxLng - minLng) >= WHOLE_TURN

  return {
    bounds: whole
      ? WORLD
      : {
          min_lng: round(wrapLongitude(minLng)),
          min_lat: round(clamp(Math.min(minLat, maxLat), -90, 90)),
          max_lng: round(wrapLongitude(maxLng)),
          max_lat: round(clamp(Math.max(minLat, maxLat), -90, 90)),
        },
    zoom: round(clamp(viewport.zoom, 0, MAX_ZOOM)),
    filters: {
      size_classes: filters.size_classes,
      independence_statuses: filters.independence_statuses,
      min_confidence: filters.min_confidence,
      direct_sales: filters.direct_sales,
      organic: filters.organic,
      biodynamic: filters.biodynamic,
      visiting: filters.visiting ?? [],
      wine_club: filters.wine_club ?? false,
    },
  }
}

// Comma-joined rather than `key[]=`: `WineryLayerQuery#allowlist` splits a string
// on commas and intersects the result with the model's own vocabulary, so this
// is the form that survives the round trip without either side depending on how
// axios serializes an array. An empty selection is omitted entirely — there is
// nothing to say and a bare `size_classes=` is one more thing for the allowlist
// to strip.
export function requestData(request: WineryRequest): LayerRequestData {
  const data: LayerRequestData = {
    min_lng: request.bounds.min_lng,
    min_lat: request.bounds.min_lat,
    max_lng: request.bounds.max_lng,
    max_lat: request.bounds.max_lat,
    zoom: request.zoom,
  }

  if (request.filters.size_classes.length > 0) data.size_classes = request.filters.size_classes.join(',')
  if (request.filters.independence_statuses.length > 0) {
    data.independence_statuses = request.filters.independence_statuses.join(',')
  }
  if (request.filters.min_confidence !== null) data.min_confidence = request.filters.min_confidence
  if (request.filters.direct_sales) data.direct_sales = true
  // Sent only when selected, like the three above: `WineryLayerQuery` casts
  // these two as booleans, so `organic=false` would be one more string to cast
  // and one more way for two requests that mean the same thing to differ.
  if (request.filters.organic) data.organic = true
  if (request.filters.biodynamic) data.biodynamic = true
  // Comma-joined like the categorical ones: `WineryLayerQuery#visiting` is an
  // allowlist over `Winery::VISITING_OPTIONS` plus `unknown`.
  if (request.filters.visiting && request.filters.visiting.length > 0) {
    data.visiting = request.filters.visiting.join(',')
  }
  if (request.filters.wine_club) data.wine_club = true

  return data
}

// An array rather than the object, so the comparison cannot come to depend on the
// order Ruby happened to build the hash in.
function key(bounds: WineryBounds, zoom: number, filters: WineryFilters): string {
  return JSON.stringify([
    bounds.min_lng,
    bounds.min_lat,
    bounds.max_lng,
    bounds.max_lat,
    zoom,
    filters.size_classes,
    filters.independence_statuses,
    filters.min_confidence,
    filters.direct_sales,
    filters.organic,
    filters.biodynamic,
    // The server always echoes `visiting`; a request built before the field
    // existed reads as the empty selection, so the two keys still agree.
    filters.visiting ?? [],
    filters.wine_club ?? false,
  ])
}

export function requestKey(request: WineryRequest): string {
  return key(request.bounds, request.zoom, request.filters)
}

// The same key off the server's echo. A response whose key differs is one the map
// has outrun — a pan races far harder than a keystroke, and drawing Bordeaux's
// counts over a viewport now showing the Mosel is worse than drawing them late,
// because the counts are then confidently wrong.
export function echoKey(layer: WineryLayer): string {
  return key(layer.bounds, layer.zoom, layer.filters)
}

// One per AXIS, not one per selected value — except the certifications, which
// are two axes rather than two values of one: a producer can be certified
// organic, biodynamic, or both, and the two columns are filtered independently.
export function activeFilterCount(filters: WineryFilters): number {
  return (
    (filters.size_classes.length > 0 ? 1 : 0) +
    (filters.independence_statuses.length > 0 ? 1 : 0) +
    (filters.min_confidence !== null ? 1 : 0) +
    (filters.direct_sales ? 1 : 0) +
    (filters.organic ? 1 : 0) +
    (filters.biodynamic ? 1 : 0) +
    ((filters.visiting?.length ?? 0) > 0 ? 1 : 0) +
    (filters.wine_club ? 1 : 0)
  )
}
